import { useState, useEffect } from "react";
import { doc, setDoc, getDoc, serverTimestamp, collection, getDocs, deleteDoc } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { useForms } from "../hooks/useForms";
import AdminLayout from "./AdminLayout";
import "./AdminDashboard.css";

export default function AdminFormsList() {
    const { forms, loading, error } = useForms();
    const [creating, setCreating] = useState(false);
    const navigate = useNavigate();

    useEffect(() => { document.title = "Salezjanie - formularze | Admin"; }, []);

    const handleCreateForm = async () => {
        const formId = prompt("Podaj skróconą nazwę formularza (będzie używana w linku, np. 'warsztaty-2025'):");
        
        if (!formId) return;

        // Validation - only lowercase alphanumeric and dashes
        const isValid = /^[a-z0-9-]+$/.test(formId);
        if (!isValid) {
            alert("Nazwa może zawierać tylko małe litery, cyfry i myślniki (-). Bez spacji i znaków specjalnych.");
            return;
        }

        setCreating(true);
        try {
            // Check if form already exists
            const formRef = doc(db, "forms", formId);
            const formSnap = await getDoc(formRef);

            if (formSnap.exists()) {
                alert(`Formularz o nazwie "${formId}" już istnieje. Wybierz inną nazwę.`);
                setCreating(false);
                return;
            }

            // Create new form
            await setDoc(formRef, {
                title: "Nowy formularz",
                subtitle: "",
                fields: [
                    {
                        id: "firstName",
                        label: "Imię",
                        type: "text",
                        required: true,
                        halfWidth: true,
                    },
                    {
                        id: "lastName",
                        label: "Nazwisko",
                        type: "text",
                        required: true,
                        halfWidth: true,
                    }
                ],
                createdAt: serverTimestamp(),
            });
            navigate(`/admin/form/${formId}/config`);
        } catch (err) {
            console.error("Błąd podczas tworzenia formularza:", err);
            alert("Nie udało się utworzyć formularza.");
        } finally {
            setCreating(false);
        }
    };

    // TEMPORARY SCRIPT - MOVE OPTIONS TO "formularz"
    const migrateOptions = async () => {
        if (!window.confirm("Czy na pewno chcesz przenieść wszystkie ustawienia ze starej kolekcji 'options' do eventu o nazwie 'formularz'?")) return;

        try {
            const snap = await getDocs(collection(db, "options"));
            let count = 0;

            for (const d of snap.docs) {
                // Skopiuj dokument do podkolekcji formularza "formularz"
                await setDoc(doc(db, "forms", "formularz", "options", d.id), d.data());
                // Usuń stary dokument z root "options"
                await deleteDoc(doc(db, "options", d.id));
                count++;
            }

            alert(`Sukces! Pomyślnie przeniesiono ${count} opcji/warsztatów.`);
        } catch (e) {
            console.error("Błąd migracji:", e);
            alert("Błąd migracji opcji: " + e.message);
        }
    };

    return (
        <AdminLayout activeTab="forms">
            <div className="config-section">
                <div className="config-header">
                    <h2 className="summary-title">Twoje formularze</h2>
                    <div className="config-actions">
                        <button
                            className="btn-small btn-cancel"
                            onClick={migrateOptions}
                        >
                            🛠️ Migruj stare OPCJE
                        </button>
                        <button
                            className="btn-small btn-save"
                            onClick={handleCreateForm}
                            disabled={creating}
                        >
                            {creating ? "Tworzenie..." : "+ Nowy formularz"}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="admin-error">⚠️ Błąd ładowania formularzy: {error}</div>
                )}

                {loading && (
                    <div className="admin-loading">
                        <div className="spinner"></div>
                        <span>Ładowanie formularzy...</span>
                    </div>
                )}

                {!loading && !error && forms.length === 0 && (
                    <div className="admin-empty">
                        <span className="empty-icon">📭</span>
                        <p>Brak formularzy. Utwórz pierwszy!</p>
                    </div>
                )}

                {!loading && !error && forms.length > 0 && (
                    <div className="table-wrapper">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Link</th>
                                    <th>Tytuł</th>
                                    <th>Podtytuł</th>
                                    <th>Opcje</th>
                                </tr>
                            </thead>
                            <tbody>
                                {forms.map((form) => (
                                    <tr key={form.id}>
                                        <td><strong>{form.id}</strong></td>
                                        <td>{form.title}</td>
                                        <td>{form.subtitle || "—"}</td>
                                        <td className="cell-actions">
                                            <Link to={`/admin/form/${form.id}`} className="btn-small btn-edit" style={{ marginRight: '0.5rem' }}>
                                                Zgłoszenia
                                            </Link>
                                            <Link to={`/admin/form/${form.id}/config`} className="btn-small btn-edit">
                                                Konfiguracja
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
